import { Attachment } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  IconButton,
  List,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useFetchData } from '../../../hooks/useFetchData';
import { useRubricCore } from '../../../hooks/useRubricCore';
import { getFolderFiles } from '../../../redux/actions/fileActions';
import { selectFiles } from '../../../redux/reducers/fileReducer';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { AppDispatch } from '../../../redux/store';
import {
  generateAnalysisReport,
  getLatestRun,
  getRunStatus,
  getScopeQuestions,
  updateAnalysisQuestionFeedback,
  updateAnalysisQuestionFiles,
} from '../../../services/dealGPT';
import { StyledLoadingButton } from '../../../theme/CustomComponents';
import {
  InitialQuestionCount,
  PageInfo,
  PageProgress,
  ProjectRunAnalysisQuestion,
  ProjectSalesforceInfo,
  ProjectWithLastScanned,
} from '../../../Types/dealGPT';
import {
  AnalysisQuestionStatus,
  AnalysisQuestionType,
  IndexType,
  ProjectRunStatus,
  RunType,
  ScopeAreasStatus,
} from '../../../Types/enums';
import { FeedbackInterface } from '../../../Types/feedback';
import { CustomFile } from '../../../Types/file';
import { getLatestQuestionVersions } from '../../../util/benchmarkUtils';
import { downloadExcel } from '../../../util/excel';
import { parseToBenchmarkSlideData } from '../../../util/powerpointPresentation/benchmarkSlide';
import { parseToControlSlideData } from '../../../util/powerpointPresentation/cybersecurity/controlSlideParser';
import {
  generateBenchmarkPresentation,
  generateControlPresentation,
} from '../../../util/powerpointPresentation/pptxDocument';
import { projectSalesforceInfoPathUrl } from '../../../util/routesUrls';
import FileCard from '../../Chat/FileCard';
import FileUploadDialog from '../../Files/FileUploadDialog';
import LoadingMask from '../../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useNavigationIndex } from '../../Providers/NavigationIndexProvider';
import AnalysisNavItem from '../PhaseTwo/AnalysisNavItem';
import Notes from '../PhaseTwo/Notes';
import StepPage from '../PhaseTwo/StepPage';
import { getLatestUniqueQuestions } from '../utils';
import { createAnalysisExcelReport } from './excel.report';
type AnalysisProps = {
  project: ProjectWithLastScanned;
};

const NavList = styled(List)({
  display: 'flex',
  gap: '2px',
  marginBottom: '24px',
});

const Analysis = ({ project }: AnalysisProps): JSX.Element => {
  const scope = useParams<{ scope?: string }>().scope || '';
  const [shouldFetchQuestions, setShouldFetchQuestions] = useState<boolean>(true);
  const [latestRunId, setLatestRunId] = useState<string>();
  const [initialQuestions, setInitialQuestions] = useState<ProjectRunAnalysisQuestion[]>();
  const [questions, setQuestions] = useState<ProjectRunAnalysisQuestion[]>();
  const [isLoadingThis, setIsLoadingThis] = useState<boolean>(false);
  const [isLoadingApplyChanges, setIsLoadingApplyChanges] = useState<boolean>(false);
  const [allQuestions, setAllQuestions] = useState<ProjectRunAnalysisQuestion[]>();
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [isScanCompleted, setIsScanCompleted] = useState<boolean>(false);
  const [isFullScan, setIsFullScan] = useState<boolean>(false);
  const [pageProgress, setPageProgress] = useState<PageProgress[]>([]);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [fileDialogOpen, setFileDialogOpen] = useState<boolean>(false);
  const [shouldRefetchFiles, setShouldRefetchFiles] = useState<boolean>(false);
  const navigate = useNavigate();
  const initialQuestionCountsRef = useRef<InitialQuestionCount[]>([]);
  const analytics = useAnalytics();
  const { stepIndex, pages, setPageNames, goToNextPage, goToPreviousPage } = useNavigationIndex();
  const [isGeneratingPresentation, setIsGeneratingPresentation] = useState<boolean>(false);
  const { rubricCoreByArea, rubricType } = useRubricCore(scope);
  const dispatch = useDispatch<AppDispatch>();
  const { folderFiles } = useSelector(selectFiles);
  const { currentFolder } = useSelector(selectFolders);
  const { data: salesforce } = useFetchData<ProjectSalesforceInfo>({
    urlPath: projectSalesforceInfoPathUrl(project.id),
  });

  const [hasFetchedFolderFiles, setHasFetchedFolderFiles] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  useEffect(() => {
    if ((!hasFetchedFolderFiles || shouldRefetchFiles) && currentFolder?.id) {
      setHasFetchedFolderFiles(true);
      setShouldRefetchFiles(false);
      setIsLoadingFiles(true);
      dispatch(getFolderFiles({ folderId: currentFolder.id })).finally(() =>
        setIsLoadingFiles(false)
      );
    }
  }, [dispatch, hasFetchedFolderFiles, shouldRefetchFiles, currentFolder?.id]);

  const isButtonDisabled = useMemo(() => {
    return (
      questions?.every(
        (question) => question.status === 'INITIAL' || question.status === 'INPUT_PENDING'
      ) || isLoadingThis
    );
  }, [questions, isLoadingThis]);

  const refetchQuestions = useCallback((isFullScan = false) => {
    initialQuestionCountsRef.current = [];
    setIsFullScan(isFullScan);
    setShouldFetchQuestions(true);
  }, []);

  const calculateProgress = (
    allQuestions: ProjectRunAnalysisQuestion[],
    initCounts: InitialQuestionCount[],
    pages: PageInfo[]
  ): PageProgress[] => {
    return pages.map((page) => {
      // Set initial values
      const initValuePage =
        initCounts.find((initCount) => initCount.pageName === page.name)?.initial ?? 0;
      const answeredValuePage =
        initCounts.find((initCount) => initCount.pageName === page.name)?.answered ?? 0;

      // Set new "answered" values
      const totalAnswered = allQuestions.filter(
        (question) =>
          [
            AnalysisQuestionStatus.ANSWERED,
            AnalysisQuestionStatus.INPUT_PENDING,
            AnalysisQuestionStatus.NEEDS_CONFIRMATION,
            AnalysisQuestionStatus.NOT_FOUND,
          ].includes(question.status) && question.pageName === page.name
      ).length;

      // Calculate progress
      let progress =
        initValuePage > 0
          ? Math.floor(((totalAnswered - answeredValuePage) / initValuePage) * 100)
          : 0;
      progress = Math.max(progress, 0);
      progress = Math.min(progress, 100);

      // Return the progress for this page
      return {
        pageName: page.name,
        progress: progress,
      };
    });
  };

  const handleGenerateReport = useCallback(async () => {
    try {
      setIsLoadingReport(true);
      analytics.event(
        AnalyticsEvent.WM_IA_A_GENERATE_REPORT,
        {
          id: project.id,
          value: scope,
        },
        {
          projectId: project.id,
          runId: latestRunId,
          scope,
        }
      );

      if (!latestRunId) {
        enqueueSnackbar('Run not found', { variant: 'error' });
        return;
      }

      const response = await generateAnalysisReport(project.id, scope, latestRunId);

      if (response.length === 0) {
        enqueueSnackbar('No data found to generate report', { variant: 'error' });
        return;
      }

      const workbook = createAnalysisExcelReport(response, scope);
      downloadExcel(
        workbook,
        `Intellio®-Advantage-${project.name}-${new Date().toISOString()}.xlsx`
      );
    } catch (error) {
      enqueueSnackbar('Failed to generate report', { variant: 'error' });
    } finally {
      setIsLoadingReport(false);
    }
  }, [project.id, scope, latestRunId, analytics, project.name]);

  useEffect(() => {
    if (initialQuestions?.length && initialQuestions.length > 0) {
      // TODO add extra logic to this map to handle whether or not its loading or not
      // see if at least one question in questions is pending or something like that

      // get distinct page names from initialQuestions
      const distinctPageNames = Array.from(
        new Set(initialQuestions?.map((question) => question.pageName))
      );

      setPageNames(
        distinctPageNames?.map((name, index) => ({
          name,
          sequence: index,
          scope: scope || '',
        })) || []
      );
    }
  }, [initialQuestions, setPageNames, scope]);

  useEffect(() => {
    const checkLatestRun = async () => {
      try {
        const runResponse = await getLatestRun({
          projectId: project.id,
          scope,
          runType: RunType.ANALYSIS,
        });

        if (runResponse.id) {
          setLatestRunId(runResponse.id);
        }
      } catch (error) {
        enqueueSnackbar('Failed to fetch latest run. Please try again later.', {
          variant: 'error',
        });
      }
    };

    let interval: ReturnType<typeof setInterval>;
    const intervalTime = 10000;

    // Get all scope questions, including all previous versions
    const fetchAllScopeQuestions = async () => {
      await checkLatestRun();

      if (!latestRunId) {
        return;
      }

      try {
        const response = await getScopeQuestions({
          projectId: project.id,
          scope,
          runId: latestRunId,
        });

        if (response.length > 0) {
          setAllQuestions(response);
          setInitialQuestions(response.filter((question) => question.status === 'INITIAL'));
          setLatestRunId(response[0].runId);

          // Determine if the run is complete
          const status = await getRunStatus(project.id, scope, response[0].runId);
          if (status !== ProjectRunStatus.CURRENTLY_SCANNING) {
            setQuestions(response);
            initialQuestionCountsRef.current = [];
            setShouldFetchQuestions(false);
            setIsLoadingThis(false);
            setIsLoadingApplyChanges(false);
            setIsScanning(false);
            setIsScanCompleted(true);
            setIsFullScan(false);
            clearInterval(interval);
          } else {
            setIsScanning(true);
            setIsScanCompleted(false);
            setIsFullScan(true);
          }
        }
      } catch (error) {
        enqueueSnackbar('Failed to fetch scope questions. Please try again later.', {
          variant: 'error',
        });
      }
    };
    if (shouldFetchQuestions) {
      setIsLoadingThis(true);
      setIsLoadingApplyChanges(true);
      fetchAllScopeQuestions();
      interval = setInterval(() => fetchAllScopeQuestions(), intervalTime);
    }

    return () => {
      clearInterval(interval);
    };
  }, [project.id, scope, latestRunId, shouldFetchQuestions, navigate]);

  const initialPageQuestions = useMemo(() => {
    if (initialQuestions && pages.length > 0) {
      return initialQuestions.filter((question) => question.pageName === pages[stepIndex].name);
    }
  }, [initialQuestions, pages, stepIndex]);

  const pageQuestions = useMemo(() => {
    if (questions && pages.length > 0) {
      return questions.filter((question) => question.pageName === pages[stepIndex].name);
    }
  }, [questions, pages, stepIndex]);

  const latestPageQuestions = useMemo(
    () => (pageQuestions ? getLatestUniqueQuestions(pageQuestions) : []),
    [pageQuestions]
  );

  const currentlySelectedFiles = useMemo(() => {
    if (latestPageQuestions.length === 0) return [];
    const fileIds = new Set(latestPageQuestions.flatMap((question) => question.fileIds ?? []));
    return folderFiles.filter((file) => fileIds.has(file.id));
  }, [latestPageQuestions, folderFiles]);

  const canAttachFiles = !isScanning;

  useEffect(() => {
    if (isScanning && isFullScan) {
      if (allQuestions && initialQuestionCountsRef.current.length > 0) {
        const progress = calculateProgress(allQuestions, initialQuestionCountsRef.current, pages);
        setPageProgress(progress);
      }

      if (!allQuestions || allQuestions.length === 0) {
        return;
      }

      const newCounts = pages.map((page) => {
        const totalInitial = allQuestions.filter(
          (question) => question.status === 'INITIAL' && question.pageName === page.name
        ).length;
        const totalAnswered = allQuestions.filter(
          (question) =>
            (question.status === 'ANSWERED' || question.status === 'INPUT_PENDING') &&
            question.pageName === page.name
        ).length;

        return {
          pageName: page.name,
          initial: totalInitial,
          answered: totalAnswered,
        };
      });

      if (initialQuestionCountsRef.current.length === 0) {
        initialQuestionCountsRef.current = newCounts;
      }
    }
  }, [allQuestions, pages, isScanning, isFullScan]);

  const handleFeedbackSubmit = async (
    feedback: FeedbackInterface,
    projectAnalysisQuestionId: string
  ) => {
    try {
      analytics.event(
        AnalyticsEvent.WM_IA_A_FEEDBACK_CLICK,
        {
          id: projectAnalysisQuestionId,
          value: `Page=${pages[stepIndex].name},Feedback=${feedback.thumbsUp}`,
        },
        {
          projectId: project.id,
          runId: latestRunId,
          scope,
          pageName: pages[stepIndex].name,
          thumbsUp: feedback.thumbsUp ?? undefined,
          otherFeedback: feedback.otherFeedback ?? undefined,
          suggestedFeedback: feedback.suggestedFeedback.toString() ?? undefined,
        }
      );
      await updateAnalysisQuestionFeedback(projectAnalysisQuestionId, feedback);

      setQuestions(
        (prevQuestions) =>
          prevQuestions?.map((question) =>
            question.id === projectAnalysisQuestionId
              ? {
                  ...question,
                  analysisQuestionFeedback: feedback,
                }
              : question
          ) || []
      );
    } catch (error) {
      enqueueSnackbar('Failed to submit feedback. Please try again later.', {
        variant: 'error',
      });
      return;
    }
  };

  const handleGenerateControlSlide = useCallback(
    async (
      slideTitleQuestion?: ProjectRunAnalysisQuestion,
      slideContentQuestion?: ProjectRunAnalysisQuestion
    ) => {
      try {
        analytics.event(
          AnalyticsEvent.WM_IA_A_GENERATE_PRESENTATION,
          {
            id: project.id,
            value: scope,
          },
          {
            projectId: project.id,
            runId: latestRunId,
            scope,
          }
        );

        if (!questions || questions.length === 0) {
          enqueueSnackbar('No questions data available', { variant: 'error' });
          return;
        }
        setIsGeneratingPresentation(true);

        if (!slideTitleQuestion || !slideContentQuestion) {
          const slideTitleQuestions = questions.filter((q) => q.promptType === 'Slide Title');
          const slideContentQuestions = questions.filter((q) => q.promptType === 'Slide Details');

          if (slideTitleQuestions.length === 0 || slideContentQuestions.length === 0) {
            enqueueSnackbar('Missing slide title or content questions', { variant: 'error' });
            return;
          }

          slideTitleQuestion = slideTitleQuestion || slideTitleQuestions[0];
          slideContentQuestion = slideContentQuestion || slideContentQuestions[0];
        }

        if (!slideTitleQuestion?.response || !slideContentQuestion?.response) {
          enqueueSnackbar('Missing slide title or content responses', { variant: 'error' });
          return;
        }

        const rubricQuestions = questions.filter((q) => q.type === AnalysisQuestionType.RUBRIC);

        const controlSlideData = await parseToControlSlideData(
          slideTitleQuestion?.response,
          slideContentQuestion?.response,
          rubricQuestions,
          rubricCoreByArea,
          rubricType
        );

        await generateControlPresentation(scope, controlSlideData);
        enqueueSnackbar('Presentation generated successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error generating presentation', { variant: 'error' });
      } finally {
        setIsGeneratingPresentation(false);
      }
    },
    [project.id, latestRunId, questions, rubricCoreByArea, rubricType, scope, analytics]
  );

  const handleGenerateBenchmarkSlide = useCallback(async () => {
    try {
      analytics.event(
        AnalyticsEvent.WM_IA_A_GENERATE_PRESENTATION,
        {
          id: project.id,
          value: scope,
        },
        {
          projectId: project.id,
          runId: latestRunId,
          scope,
        }
      );

      const benchmarkQuestions = getLatestQuestionVersions(
        questions?.filter((q) => q.type === AnalysisQuestionType.BENCHMARK) ?? []
      );

      if (!benchmarkQuestions || benchmarkQuestions.length === 0) {
        enqueueSnackbar('There is no data available to generate benchmark slide', {
          variant: 'error',
        });
        return;
      }

      setIsGeneratingPresentation(true);

      const data = await parseToBenchmarkSlideData(benchmarkQuestions, salesforce);

      const initialQ =
        benchmarkQuestions.find((q) => q.status === AnalysisQuestionStatus.INITIAL) ??
        benchmarkQuestions[0];

      const benchmarkDate = new Date(initialQ.createdAt);

      await generateBenchmarkPresentation(scope, benchmarkDate, data);
      enqueueSnackbar('Presentation generated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error generating presentation', { variant: 'error' });
    } finally {
      setIsGeneratingPresentation(false);
    }
  }, [project.id, latestRunId, questions, scope, salesforce, analytics]);

  // Helper function to determine scope status
  const scopeStatus = useMemo(() => {
    if (!questions || questions.length === 0) return ScopeAreasStatus.NOT_STARTED;

    // Group questions by pageName
    const questionsByPage = questions.reduce<Record<string, ProjectRunAnalysisQuestion[]>>(
      (acc, question) => {
        (acc[question.pageName] = acc[question.pageName] || []).push(question);
        return acc;
      },
      {}
    );

    // Check if all questions are "INITIAL"
    if (questions.every((q) => q.status === 'INITIAL' || q.status === 'INPUT_PENDING')) {
      return ScopeAreasStatus.NOT_STARTED;
    }

    // Find pages with not initial questions
    const inProgressPages = Object.entries(questionsByPage)
      .filter(([pageName]) => pageName !== 'Site Visit Notes' && pageName !== 'Flash Report')
      .filter(([, pageQuestions]) =>
        pageQuestions.some((q) => q.status !== 'INITIAL' && q.status !== 'INPUT_PENDING')
      );

    // Find pages with all initial questions
    const notStartedPages = Object.entries(questionsByPage)
      .filter(([pageName]) => pageName !== 'Site Visit Notes' && pageName !== 'Flash Report')
      .filter(([, pageQuestions]) =>
        pageQuestions.every((q) => q.status === 'INITIAL' || q.status === 'INPUT_PENDING')
      );

    // If there are pages with not initial questions and pages with all initial questions
    if (inProgressPages.length > 0 && notStartedPages.length > 0)
      return ScopeAreasStatus.IN_PROGRESS;

    return ScopeAreasStatus.COMPLETE;
  }, [questions]);

  const handleFileSelectionConfirm = useCallback(
    async (selectedFiles: CustomFile[]) => {
      if (latestPageQuestions.length === 0) return;

      const newFileIds = selectedFiles.map((file) => file.id);
      const questionIds = latestPageQuestions.map((q) => q.id);

      try {
        await updateAnalysisQuestionFiles(questionIds, newFileIds);

        setQuestions(
          (prev) =>
            prev?.map((q) =>
              latestPageQuestions.some((lq) => lq.id === q.id) ? { ...q, fileIds: newFileIds } : q
            ) ?? []
        );

        setFileDialogOpen(false);
      } catch (error) {
        enqueueSnackbar('Failed to update file selection', { variant: 'error' });
      }
    },
    [latestPageQuestions]
  );

  return (
    <div>
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <div style={{ display: 'flex' }}>
          <Typography variant="h2" sx={{ color: 'grey', paddingRight: '10px', fontSize: '24px' }}>
            Scope:
          </Typography>
          <Typography variant="h2" sx={{ fontSize: '24px' }}>
            {scope}
          </Typography>
        </div>
        <div
          style={{
            marginRight: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <StyledLoadingButton
            variant="outlined"
            loading={isLoadingReport}
            loadingPosition="end"
            endIcon={<></>}
            onClick={() => handleGenerateReport()}
            disabled={isButtonDisabled}
          >
            Generate Report
          </StyledLoadingButton>
        </div>
      </div>
      <NavList>
        {pages.map((page, index) => (
          <AnalysisNavItem
            index={index}
            stepIndex={stepIndex}
            projectId={project.id}
            runId={latestRunId}
            scope={scope}
            pageName={page.name}
            scopeStatus={scopeStatus}
            key={page.name}
            isLoadingThis={isLoadingThis}
            isLoadingApplyChanges={isLoadingApplyChanges}
            pageQuestions={questions?.filter((question) => question.pageName === page.name) ?? []}
            isScanning={isScanning}
            isScanCompleted={isScanCompleted}
            progress={pageProgress.find((p) => p.pageName === page.name)?.progress || 0}
            isFullScan={isFullScan}
          />
        ))}
      </NavList>
      <Container
        sx={{
          backgroundColor: '#ffffff',
          padding: '32px',
        }}
      >
        {project.indexToUse !== IndexType.GRAPH_RAG &&
          pages[stepIndex]?.name !== 'Site Visit Notes' && (
            <>
              <Box display="flex" alignItems="center" zIndex={canAttachFiles ? 10 : 0}>
                <Typography variant="subtitle2">Attached Files</Typography>
                <Tooltip
                  PopperProps={{ placement: 'top' }}
                  id={'analysis-upload-document-label'}
                  title={<Typography variant="caption">Add Documents</Typography>}
                >
                  <span>
                    <IconButton
                      aria-labelledby={'analysis-upload-document-label'}
                      disabled={!canAttachFiles}
                      onClick={() => {
                        if (!canAttachFiles) return;

                        analytics.event(
                          AnalyticsEvent.WM_IA_A_ADD_MORE_FILES,
                          {},
                          { projectId: project.id, pageName: pages[stepIndex]?.name }
                        );
                        setFileDialogOpen(true);
                      }}
                    >
                      <Attachment />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box display="flex" gap={2} mb={2} zIndex={10} overflow="auto">
                {currentlySelectedFiles.map((file) => (
                  <div
                    key={file.id}
                    style={{
                      padding: '5px',
                      flexShrink: 0,
                    }}
                  >
                    <FileCard uploadedFile={file} />
                  </div>
                ))}
              </Box>
            </>
          )}
        {stepIndex > 0 && (
          <Button
            variant="outlined"
            onClick={goToPreviousPage}
            style={{
              position: 'absolute',
              right: '130px',
              zIndex: canAttachFiles ? 10 : 0,
            }}
          >
            Previous Page
          </Button>
        )}
        {stepIndex !== pages.length - 1 && (
          <Button
            variant="contained"
            onClick={goToNextPage}
            style={{ position: 'absolute', right: '24px', zIndex: canAttachFiles ? 10 : 0 }}
          >
            Next Page
          </Button>
        )}
        {stepIndex == pages.length - 1 && (
          <Button
            variant="contained"
            onClick={goToNextPage}
            disabled={isButtonDisabled}
            style={{ minWidth: '97px', position: 'absolute', right: '24px', zIndex: 10 }}
          >
            Finish
          </Button>
        )}
        {/* ASSUMPTION - notes will always be the first step */}
        {stepIndex === 0 && (
          <Notes
            projectId={project.id}
            scope={scope ?? ''}
            question={initialQuestions?.find(
              (question) => question.type === AnalysisQuestionType.NOTES
            )}
            refetchQuestions={refetchQuestions}
            latestRunId={latestRunId || undefined}
            firstRun={
              questions?.every(
                (question) => question.status === 'INITIAL' || question.status === 'INPUT_PENDING'
              ) || isLoadingThis
            }
            isParentLoading={isLoadingThis}
          />
        )}
        {stepIndex > 0 && (
          <>
            {scopeStatus !== ScopeAreasStatus.COMPLETE && <LoadingMask isLoading={null} />}
            <StepPage
              page={pages[stepIndex]}
              initialPageQuestions={initialPageQuestions}
              pageQuestions={pageQuestions}
              latestRunId={latestRunId}
              project={project}
              setIsLoadingThis={setIsLoadingThis}
              setIsLoadingApplyChanges={setIsLoadingApplyChanges}
              refetchQuestions={refetchQuestions}
              handleFeedbackSubmit={handleFeedbackSubmit}
              setShouldFetchQuestions={setShouldFetchQuestions}
              handleGenerateControlSlide={handleGenerateControlSlide}
              handleGenerateBenchmarkSlide={handleGenerateBenchmarkSlide}
              isGeneratingPresentation={isGeneratingPresentation}
            />
          </>
        )}
      </Container>
      <LoadingMask isLoading={isLoadingThis} />
      {project.indexToUse !== IndexType.GRAPH_RAG && (
        <FileUploadDialog
          userFiles={[]} // For analysis, we only want to use folder files
          folderFiles={folderFiles}
          currentlySelectedFiles={currentlySelectedFiles}
          setShouldRefetchFiles={setShouldRefetchFiles}
          openDialog={fileDialogOpen && canAttachFiles}
          isLoading={isLoadingFiles}
          setIsLoading={setIsLoadingFiles}
          buttonConfirmText="Use These Files"
          handleCloseDialog={() => setFileDialogOpen(false)}
          handleConfirmDialog={handleFileSelectionConfirm}
          showSearch={true}
          folderFilesOnly
        />
      )}
    </div>
  );
};

export default Analysis;
